import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { env } from '../env';
import { getAccessToken } from '../auth/auth0Client';

/**
 * Shared Axios instance. Mirrors the pattern from frontend-prod/lib/api.ts:
 *   - Request interceptor injects Bearer token from Auth0 SDK
 *   - Response interceptor retries once on 401 with a fresh token
 *
 * The token-refresh heavy lifting is handled inside auth0-spa-js via
 * `getTokenSilently()` (rotation-aware). We don't have to manage expiry
 * manually.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: env.apiUrl,
  timeout: 60_000, // generous — SSE is separate; only long POSTs like S3 presign hit this
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    // Workaround for WKWebView (Word on Mac) hangs: some builds hold HTTP/2
    // connections open across preflights and then drop responses silently.
    // Forcing Connection: close downgrades keep-alive; combined with Cache-Control
    // it discourages the WebView from reusing a broken socket.
    'Cache-Control': 'no-cache',
  },
});

apiClient.interceptors.request.use(async (config) => {
  try {
    const token = await getAccessToken();
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  } catch {
    // Let the request go through unauthed — backend will 401 and we'll surface login.
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalConfig = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    if (error.response?.status === 401 && originalConfig && !originalConfig._retried) {
      originalConfig._retried = true;
      try {
        const token = await getAccessToken();
        originalConfig.headers = originalConfig.headers ?? {};
        (originalConfig.headers as Record<string, string>).Authorization = `Bearer ${token}`;
        return apiClient.request(originalConfig);
      } catch {
        // fall through to reject
      }
    }
    return Promise.reject(error);
  },
);
